import React, { useState, useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { Button, Container } from 'react-bootstrap';
import { userApi } from '../../../../../Utils/API';
import {
  GET_CLOUD_SERVICE_TNC_LOGIN_ALERT,
  ACCEPT_CLOUD_SERVICE_TNC,
} from '../../../../../Utils/GlobalConstants';
import { sanitizeHTML } from '../../../../../Utils/Sanitize';
import styles from './styles.module.scss';

/**
 * CloudTermsAndConditionLoginAlert
 * ---------------------------------
 * Post-login popup that displays cloud-service Terms & Conditions that the
 * user must mandatorily accept before dismissing.
 *
 * Behaviour:
 *  - On authenticated login, calls GET /user/api/terms-and-conditions/cloud-service/login-alert.
 *  - 204 → nothing to show; component stays hidden.
 *  - 200 → returns an array of { integrationCode, htmlContent }.
 *      Each item is rendered as its own Popup (nested on top of each other).
 *      If there are 2 items, 2 popups are visible simultaneously — stacked.
 *  - Each popup has NO close icon, NO "Remind Later", NO closeOnDocumentClick,
 *      NO closeOnEscape. The user MUST scroll to the bottom and click Accept.
 *  - On Accept: POST /user/api/terms-and-conditions/cloud-service/accepted
 *      with { integrationCode, browser, moduleType: "LoginAlert" }.
 *      The individual popup closes after successful POST.
 *
 * Scroll gate (same pattern as GCPTnCPopup):
 *  - "Accept" is disabled until scrollHeight - scrollTop - clientHeight < 5px.
 *  - If content is short enough that no scrollbar appears, Accept is auto-enabled.
 *
 * Props:
 *  @param {boolean}  customerIsAuthenticated - triggers the initial GET call.
 *  @param {function} Logout                  - standard 401 logout handler.
 */

/**
 * SingleTnCPopup
 * --------------
 * Renders one mandatory TnC popup for a single { integrationCode, htmlContent } item.
 * The parent maps over the items array and renders one of these per item.
 */
const SingleTnCPopup = ({ item, isOpen, onAccepted, Logout }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef(null);

  const userAgent = navigator.userAgent;
  const browserName = userAgent.match(/(Chrome|Safari|Firefox|Edge|Opera|Internet Explorer)\//);
  const browserVersion = userAgent.match(/\((.*?)\)/);

  const config = {
    headers: {
      Authorization: `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`,
    },
  };

  // Reset scroll state and scroll to top when popup opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  // Auto-enable Accept if content is short enough to not require scrolling
  useEffect(() => {
    if (isOpen && item?.htmlContent && scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true);
      }
    }
  }, [isOpen, item]);

  // Scroll detection — 5px tolerance for sub-pixel rounding
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 5) {
      setHasScrolledToBottom(true);
    }
  };

  // POST acceptance for this specific TnC item
  async function handleAccept() {
    const browser =
      browserName && browserVersion
        ? `${browserName[1]} ${browserVersion[1]}`
        : navigator.userAgent;

    const body = {
      integrationCode: item.integrationCode,
      browser: browser,
      moduleType: 'General',
    };

    await userApi
      .post(ACCEPT_CLOUD_SERVICE_TNC, body, config)
      .then((resp) => {
        if (resp.status === 200 || resp.status === 204) {
          onAccepted(item.integrationCode);
        }
      })
      .catch((error) => {
        if (error?.status === 401 || error?.response?.status === 401) {
          Logout();
        } else {
          // On other errors, still dismiss the popup — do not block the user
          onAccepted(item.integrationCode);
        }
      });
  }

  return (
    <Popup
      className="custom-modal custom-modal-xl"
      open={isOpen}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      modal
      nested
    >
      {() => (
        <div className="modal terms-conditions">
          {/* ---- Header ---- */}
          <div className="header">
            <h2 className="mb-0">Terms & Conditions</h2>
          </div>

          {/* ---- Scrollable TnC Content ---- */}
          <Container fluid className="p-0">
            <div
              ref={scrollContainerRef}
              className={styles['tnc-scroll-container']}
              onScroll={handleScroll}
            >
              {item?.htmlContent ? (
                <div
                  className="px-4 py-3"
                  dangerouslySetInnerHTML={sanitizeHTML(item.htmlContent)}
                />
              ) : (
                <p className="text-center py-5 text-muted">
                  Unable to load terms. Please try again later.
                </p>
              )}
            </div>

            {/*
              Fixed-height warning area — always rendered to prevent layout shift.
              Yellow when scroll still needed; plain white once user has scrolled to bottom.
            */}
            <div
              className={
                !hasScrolledToBottom
                  ? styles['tnc-scroll-warning']
                  : styles['tnc-scroll-warning-empty']
              }
            >
              {!hasScrolledToBottom && (
                <>
                  <span className={styles['tnc-warning-icon']}>!</span>
                  &nbsp;Please scroll to the bottom to read the full terms before accepting
                </>
              )}
            </div>

            {/* ---- Footer with Accept button only — no close, no remind later ---- */}
            <div
              className={
                !hasScrolledToBottom
                  ? styles['tnc-footer-bordered']
                  : styles['tnc-footer']
              }
            >
              <Button
                className="px-4"
                disabled={!hasScrolledToBottom}
                onClick={handleAccept}
                variant={hasScrolledToBottom ? 'primary' : 'light'}
              >
                Accept
              </Button>
            </div>
          </Container>
        </div>
      )}
    </Popup>
  );
};

/**
 * CloudTermsAndConditionLoginAlert
 * Main component — fetches TnC items and renders one SingleTnCPopup per item.
 * All popups are visible simultaneously, stacked on top of each other.
 */
const CloudTermsAndConditionLoginAlert = ({ customerIsAuthenticated, Logout }) => {
  // Full array of { integrationCode, htmlContent } from the API
  const [tncItems, setTncItems] = useState([]);

  // Set of integrationCodes that have been accepted (used to control open state per popup)
  const [acceptedCodes, setAcceptedCodes] = useState(new Set());

  const config = {
    headers: {
      Authorization: `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`,
    },
  };

  // GET — fetch login-alert TnC items on authentication
  async function fetchLoginAlertTnC() {
    await userApi
      .get(GET_CLOUD_SERVICE_TNC_LOGIN_ALERT, config)
      .then((resp) => {
        if (resp.status === 204 || !resp.data || resp.data.length === 0) {
          // No active TnC popups — nothing to show
          return;
        }
        setTncItems(resp.data);
        setAcceptedCodes(new Set());
      })
      .catch((error) => {
        if (error?.status === 401 || error?.response?.status === 401) {
          Logout();
        }
        // Any other error: silently skip — do not block the user
      });
  }

  useEffect(() => {
    if (customerIsAuthenticated) {
      fetchLoginAlertTnC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerIsAuthenticated]);

  // Called by SingleTnCPopup when user accepts a specific item
  function handleItemAccepted(integrationCode) {
    setAcceptedCodes((prev) => new Set([...prev, integrationCode]));
  }

  if (tncItems.length === 0) {
    return null;
  }

  return (
    <>
      {tncItems.map((item) => (
        <SingleTnCPopup
          key={item.integrationCode}
          item={item}
          isOpen={!acceptedCodes.has(item.integrationCode)}
          onAccepted={handleItemAccepted}
          Logout={Logout}
        />
      ))}
    </>
  );
};

export default CloudTermsAndConditionLoginAlert;
